import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import colors from "../design/colors";

interface ImageCircleProps {
  src: string;
  size: number | string;
  style?: React.CSSProperties;
  onClick?: () => void;
  borderColor?: string;
  borderWidth?: number | string;
  showBorder?: boolean;
}

const ImageCircle = ({
  src,
  size,
  style,
  onClick,
  borderColor,
  borderWidth,
  showBorder,
}: ImageCircleProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);

  // src 변경 시 useEffect 대신 렌더 중 동기 리셋 (onLoad와의 race 방지)
  if (loadedSrc !== src && loaded) {
    setLoaded(false);
    setLoadedSrc(src);
  }

  useEffect(() => {
    if (inView && imgRef.current?.complete) {
      setLoaded(true);
    }
  }, [inView, src]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Image
      ref={ref}
      $size={size}
      style={style}
      onClick={onClick}
      $borderColor={borderColor}
      $borderWidth={borderWidth}
      $showBorder={showBorder}
    >
      {!loaded && <Skeleton />}
      {inView && (
        <img
          key={src}
          ref={imgRef}
          src={src}
          alt=""
          style={{ opacity: loaded ? 1 : 0, transition: "opacity 200ms" }}
          onLoad={() => {
            setLoaded(true);
            setLoadedSrc(src);
          }}
          onError={() => {
            setLoaded(true);
            setLoadedSrc(src);
          }}
        />
      )}
    </Image>
  );
};

export default ImageCircle;

const Image = styled.div<{
  $size: number | string;
  $borderColor?: string;
  $borderWidth?: number | string;
  $showBorder?: boolean;
}>`
  width: ${(props) =>
    typeof props.$size === "number" ? `${props.$size}px` : props.$size};
  height: ${(props) =>
    typeof props.$size === "number" ? `${props.$size}px` : props.$size};
  border-radius: 50%;
  overflow: hidden;
  background-color: ${colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  position: relative;
  flex-shrink: 0;
  ${(props) =>
    props.$showBorder
      ? `border: ${props.$borderWidth || 1}px solid ${
          props.$borderColor || colors.gray100
        };`
      : ""}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const Skeleton = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    ${colors.gray100} 25%,
    ${colors.gray50} 50%,
    ${colors.gray100} 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
